import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import useDataFetch from "../hooks/useDataFetch";
import { MobileResponsive } from "./MobileResponsive";
import DynamicMetaTags from "../components/DynamicMetaTags";
import Avatar from "react-avatar";

const ManageOutlets = () => {
  const navigate = useNavigate();

  const { data, isLoading } = useDataFetch("/admin/outlet-onboarding-requests");

  const outlets = data?.outlets || [];
  const [filteredOutlets, setFilteredOutlets] = useState([]);
  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    setFilteredOutlets(outlets);
  }, [data]);

  const handleSearch = (value) => {
    setSearchValue(value);
    if (value.trim() === "") {
      setFilteredOutlets(outlets);
    } else {
      setFilteredOutlets(
        outlets.filter((o) =>
          o?.outlet_name?.toLowerCase().includes(value.toLowerCase())
        )
      );
    }
  };

  if (isLoading) {
    return (
      <>
        <MobileResponsive />
        <div className="lg:flex hidden items-start">
          <Sidebar />
          <div className="ml-[14.5vw] w-full h-screen flex items-center justify-center bg-gray-50">
            <div className="flex flex-col items-center gap-2">
              <div className="h-12 w-12 rounded-full border-4 border-t-quaternary border-b-quaternary border-r-transparent border-l-transparent animate-spin"></div>
              <p className="text-gray-600 font-medium">Loading..</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <DynamicMetaTags
        pageTitle="Beep - Review and Onboard New Outlets"
        pageDescription="Take control of your outlets with Beep! Effortlessly manage new outlet requests, view detailed outlet information, track payment history, and set pickup prices. Enable or disable outlets with a click!"
        pageKeywords="Beep, outlet management, new outlet requests, outlet details, payment history, set pickup prices, enable outlets, disable outlets, active outlets"
      />
      <MobileResponsive />
      <div className="lg:flex hidden items-start">
        <Sidebar />

        <div className="ml-[14.5vw] w-full h-screen overflow-y-auto px-10 py-8 text-secondary">
          <div className="flex justify-between items-center">
            <Link to="/outlets" className="flex items-center gap-3">
              <div className="bg-[#F75E00] w-5 h-5 flex items-center justify-center rounded-full pr-[1.5px]">
                <img src="/assets/left-white-arrow.svg" alt="" className="w-[6px]" />
              </div>
              <span className="font-semibold text-base">New Outlet Requests</span>
            </Link>

            <div className="flex items-center">
              <form
                className="flex items-center gap-2 rounded-full px-3 border-2 border-[#F7631D] w-[30vw]"
                onSubmit={(e) => e.preventDefault()}
              >
                <div className="relative w-full">
                  <input
                    type="text"
                    placeholder="Search outlet name"
                    className="peer rounded-full py-2 text-[#F7631D] placeholder:text-transparent text-base font-medium w-full outline-none px-1 placeholder:select-none"
                    value={searchValue}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  <label
                    className={`absolute left-1 transform transition-all duration-200 ${
                      searchValue
                        ? "-top-0.5 -translate-y-1/2 left-2 text-xs px-1 bg-white rounded-full"
                        : "top-1/2 -translate-y-1/2 left-3 text-base"
                    } text-[#F7631D] pointer-events-none`}
                  >
                    Search outlet name
                  </label>
                </div>
                <button type="submit">
                  <img src="/assets/search.svg" alt="Search" />
                </button>
              </form>
            </div>

            <div className="flex items-center text-[#413F3F]">
              <span className="font-semibold text-base">
                <span className="font-bold">{data?.count ?? outlets.length}</span>{" "}
                {(data?.count ?? outlets.length) !== 1 ? "Requests" : "Request"}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-x-3 gap-y-5 w-[85%] py-8">
            {filteredOutlets.length > 0 ? (
              filteredOutlets.map((outlet) => (
                <div
                  key={outlet?.id}
                  className="bg-white rounded-lg flex items-center justify-between border-[0.5px] border-[#00000014] shadow-custom4 px-5 py-2.5 cursor-pointer"
                  onClick={() => navigate(`/manage-outlets/${outlet?.id}`)}
                >
                  <div className="flex items-center gap-5">
                    <Avatar
                      name={outlet?.outlet_name}
                      size="52"
                      round={true}
                      maxInitials={2}
                      textSizeRatio={2.5}
                      style={{ letterSpacing: "1px" }}
                    />
                    <div className="flex flex-col gap-0.5">
                      <p className="text-[15px] font-semibold tracking-wide capitalize">
                        {outlet?.outlet_name}
                      </p>
                      <p className="text-xs text-gray-400">{outlet?.phone}</p>
                    </div>
                  </div>
                  <img src="/assets/right-arrow.svg" alt="" className="h-[26px]" />
                </div>
              ))
            ) : (
              <div className="col-span-3 flex items-center justify-center py-10">
                <p className="text-gray-600 font-medium">No new outlet requests</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ManageOutlets;
